import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, getDocs, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useLang } from '../LanguageContext';
import { t } from '../i18n';

const COULEUR = '#C0392B';
const VERT = '#3B6D11';

const OBJECTIFS_VISITE = ['Présentation produits', 'Suivi commande', 'Relance devis', 'Livraison', 'Recouvrement', 'Prospection', 'Réclamation', 'Autre'];
const RESULTATS_VISITE = ['Commande passée', 'Devis demandé', 'Rendez-vous planifié', 'Pas intéressé', 'Absent', 'Promesse de commande', 'En attente', 'Autre'];
const PROCHAINES_ACTIONS = ['Rappeler', 'Envoyer devis', 'Livrer', 'Relancer', 'Présenter nouveau produit', 'Recouvrement', 'Aucune action', 'Autre'];
const RESPONSABLES = ['Commercial', 'Client', 'Direction', 'Logistique'];

export default function FormulaireVisite({ user, client, onClose, onSave }) {
  const [etape, setEtape] = useState(1);
  const { lang } = useLang();
  const [loading, setLoading] = useState(false);
  const [heureDebut] = useState(new Date());
  const [gps, setGps] = useState(null);
  const [gpsDebut, setGpsDebut] = useState(null);
  const [produits, setProduits] = useState([]);
  const [commande, setCommande] = useState({});
  const [commandeAutre, setCommandeAutre] = useState('');

  // Capture GPS au démarrage
  React.useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          const gpsData = { lat: pos.coords.latitude, lng: pos.coords.longitude, precision: Math.round(pos.coords.accuracy) };
          setGpsDebut(gpsData);
          try {
            await addDoc(collection(db, 'tracking'), {
              commercialId: user.uid,
              commercialNom: `${user.prenom || ''} ${user.nom || ''}`.trim(),
              lat: pos.coords.latitude, lng: pos.coords.longitude,
              precision: Math.round(pos.coords.accuracy),
              timestamp: serverTimestamp(),
              date: new Date().toISOString().split('T')[0],
              source: 'visite',
            });
          } catch(e) { console.log('Tracking:', e.message); }
        },
        (err) => console.log('GPS:', err.message),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
      );
    }
  }, []);

  // Charger produits Firebase
  useEffect(() => {
    const chargerProduits = async () => {
      try {
        const snap = await getDocs(collection(db, 'produits'));
        setProduits(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch(e) { console.log('Produits:', e); }
    };
    chargerProduits();
  }, []);

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition(pos => {
      setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude });
    });
  }, []);

  const [form, setForm] = useState({
    statutClient: client?.statut || '',
    objectifVisite: '', objectifAutre: '',
    resultat: '', resultatAutre: '',
    commentaire: '',
    prochaineAction: '', prochaineActionAutre: '',
    commentaireAction: '',
    dateAction: '', responsable: '', rappels: [],
  });

  // Vocal
  const [enregistrement, setEnregistrement] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [dureeVocal, setDureeVocal] = useState(0);
  const mediaRecorderRef = useRef(null);
  const timerRef = useRef(null);

  const demarrerVocal = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      recorder.ondataavailable = e => chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      recorder.start();
      mediaRecorderRef.current = recorder;
      setEnregistrement(true);
      setDureeVocal(0);
      timerRef.current = setInterval(() => setDureeVocal(d => d + 1), 1000);
    } catch (e) { alert('Microphone non accessible'); }
  };

  const arreterVocal = () => {
    mediaRecorderRef.current?.stop();
    clearInterval(timerRef.current);
    setEnregistrement(false);
  };

  // Arrêt automatique à 60 s (limite de stockage Firestore : 1 Mo par document)
  useEffect(() => {
    if (enregistrement && dureeVocal >= 60) arreterVocal();
  }, [enregistrement, dureeVocal]);

  const toggleRappel = (val) => {
    setForm(f => ({ ...f, rappels: f.rappels.includes(val) ? f.rappels.filter(r => r !== val) : [...f.rappels, val] }));
  };

  const updateCommande = (produitId, qte) => {
    setCommande(c => {
      const next = { ...c };
      if (!qte || qte === '0') delete next[produitId];
      else next[produitId] = qte;
      return next;
    });
  };

  const partagerWhatsApp = () => {
    const dateCmd = new Date().toLocaleDateString('fr-FR');
    const dateLiv = form.dateAction || 'À définir';
    const lignes = produits
      .filter(p => commande[p.id] && commande[p.id] !== '0')
      .map(p => `- ${p.nom} : ${commande[p.id]} ${p.unite || 'Sac'}`);
    if (commandeAutre.trim()) lignes.push(`- Autre : ${commandeAutre.trim()}`);
    if (lignes.length === 0) { alert('Ajoutez au moins un produit à la commande'); return; }
    const msg = [
      `Client : ${client?.nom || ''}`,
      `Date : ${dateCmd}`,
      `Livraison souhaitée : ${dateLiv}`,
      ``,
      ...lignes,
    ].join('\n');
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  // Convertit les libellés de rappel en dates réelles (YYYY-MM-DD) à partir de la date d'action
  const calculerDatesRappels = (labels, dateActionStr) => {
    if (!dateActionStr || !Array.isArray(labels)) return [];
    const OFFSETS = { 'La veille': 1, '2 jours avant': 2, '1 semaine avant': 7 };
    return labels
      .filter(l => OFFSETS[l] !== undefined)
      .map(l => {
        const d = new Date(dateActionStr + 'T00:00:00');
        d.setDate(d.getDate() - OFFSETS[l]);
        return d.toISOString().split('T')[0];
      });
  };

  const enregistrerVisite = async () => {
    setLoading(true);
    try {
      const heureFin = new Date();
      const lignesCommande = produits
        .filter(p => commande[p.id])
        .map(p => ({ produitId: p.id, nom: p.nom, quantite: commande[p.id], unite: p.unite || 'Sac' }));

      // Conversion du vocal en base64 pour stockage direct dans Firestore
      // (limite Firestore : 1 Mo par document → vocal plafonné à 60 s / 700 Ko)
      let vocalData = null;
      if (audioBlob) {
        if (audioBlob.size > 700 * 1024) {
          alert('⚠️ Vocal trop long pour être sauvegardé — la visite sera enregistrée sans le vocal.');
        } else {
          try {
            vocalData = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = () => reject(new Error('Lecture audio impossible'));
              reader.readAsDataURL(audioBlob);
            });
          } catch (e) {
            console.log('Conversion vocal échouée:', e.message);
            alert('⚠️ Le vocal n\'a pas pu être sauvegardé. La visite sera enregistrée sans le vocal.');
          }
        }
      }

      const visite = {
        commercialId: user.uid,
        commercialNom: `${user.prenom} ${user.nom}`,
        clientId: client?.id || null,
        clientNom: client?.nom || 'Client inconnu',
        date: serverTimestamp(),
        heureDebut: heureDebut.toISOString(),
        heureFin: heureFin.toISOString(),
        duree: Math.round((heureFin - heureDebut) / 60000),
        gps: gps || null, gpsDebut: gpsDebut || null,
        statutClient: form.statutClient,
        objectifVisite: form.objectifVisite === 'Autre' ? form.objectifAutre : form.objectifVisite,
        resultat: form.resultat === 'Autre' ? form.resultatAutre : form.resultat,
        commentaire: form.commentaire,
        prochaineAction: form.prochaineAction === 'Autre' ? form.prochaineActionAutre : form.prochaineAction,
        commentaireAction: form.commentaireAction,
        dateAction: form.dateAction,
        responsable: form.responsable,
        rappels: form.rappels,
        statut: 'REALISEE',
        commande: lignesCommande.length > 0 ? lignesCommande : null,
        commandeAutre: commandeAutre.trim() || null,
        vocalData: vocalData,
        vocalDuree: vocalData ? dureeVocal : null,
      };

      const docRef = await addDoc(collection(db, 'visites'), visite);

      if (form.prochaineAction && form.prochaineAction !== 'Aucune action' && form.dateAction) {
        await addDoc(collection(db, 'actions'), {
          commercialId: user.uid,
          clientId: client?.id || null,
          clientNom: client?.nom || 'Client inconnu',
          action: visite.prochaineAction,
          dateAction: form.dateAction,
          responsable: form.responsable,
          rappels: form.rappels,
          rappelsDates: calculerDatesRappels(form.rappels, form.dateAction),
          statut: 'A FAIRE',
          visiteId: docRef.id,
          createdAt: serverTimestamp(),
        });
      }

      // Sauvegarder commande dans Firebase si commande passée
      if (form.resultat === 'Commande passée' && (lignesCommande.length > 0 || commandeAutre.trim())) {
        await addDoc(collection(db, 'commandes'), {
          commercialId: user.uid,
          commercialNom: `${user.prenom} ${user.nom}`,
          clientId: client?.id || null,
          clientNom: client?.nom || 'Client inconnu',
          visiteId: docRef.id,
          lignes: lignesCommande,
          commandeAutre: commandeAutre.trim() || null,
          dateLivraison: form.dateAction || null,
          createdAt: serverTimestamp(),
          date: new Date().toISOString().split('T')[0],
          statut: 'En attente',
        });
      }

      if (client?.id && form.statutClient) {
        await updateDoc(doc(db, 'clients', client.id), { statut: form.statutClient });
      }

      onSave && onSave(visite);
      onClose();
    } catch (e) { alert('Erreur : ' + e.message); }
    setLoading(false);
  };

  const peutAvancer = () => {
    if (etape === 1) return form.statutClient !== '';
    if (etape === 2) return form.objectifVisite !== '';
    if (etape === 3) return form.resultat !== '';
    if (etape === 4) return form.prochaineAction !== '';
    if (etape === 5) return form.prochaineAction === 'Aucune action' || (form.dateAction !== '' && form.responsable !== '');
    return true;
  };

  const nbProduitsCommande = Object.keys(commande).filter(k => commande[k] && commande[k] !== '0').length + (commandeAutre.trim() ? 1 : 0);
  const motiv = ['💪 Super visite !', '🔥 Tu gères !', '⭐ Excellent travail !', '🚀 En route pour la prochaine !'];

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
          <div style={styles.headerInfo}>
            <div style={styles.headerTitle}>Saisie de visite</div>
            <div style={styles.headerSub}>{client?.nom || 'Client'}</div>
          </div>
          <div style={styles.etapeInfo}>{etape}/6</div>
        </div>

        <div style={styles.progressBar}>
          {[1,2,3,4,5,6].map(n => (
            <div key={n} style={{ ...styles.progressDot, background: n <= etape ? COULEUR : '#e0e0e0' }} />
          ))}
        </div>

        <div style={styles.content}>

          {/* ÉTAPE 1 : STATUT */}
          {etape === 1 && (
            <div style={styles.etapeWrap}>
              <div style={styles.etapeTitle}>👤 Statut du client</div>
              <div style={styles.etapeSub}>Heure de début : {heureDebut.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</div>
              <div style={{ fontSize: 11, color: gpsDebut ? VERT : '#888', marginTop: 4 }}>
                {gpsDebut ? `📍 Position capturée (±${gpsDebut.precision}m)` : '📍 Acquisition GPS...'}
              </div>
              {['Prospect', 'Client actif', 'Client inactif', 'Nouveau client'].map(s => (
                <button key={s} style={{ ...styles.choiceBtn, ...(form.statutClient === s ? styles.choiceBtnActive : {}) }}
                  onClick={() => setForm(f => ({ ...f, statutClient: s }))}>{s}</button>
              ))}
            </div>
          )}

          {/* ÉTAPE 2 : OBJECTIF */}
          {etape === 2 && (
            <div style={styles.etapeWrap}>
              <div style={styles.etapeTitle}>🎯 Objectif de la visite</div>
              {OBJECTIFS_VISITE.map(o => (
                <button key={o} style={{ ...styles.choiceBtn, ...(form.objectifVisite === o ? styles.choiceBtnActive : {}) }}
                  onClick={() => setForm(f => ({ ...f, objectifVisite: o }))}>{o}</button>
              ))}
              {form.objectifVisite === 'Autre' && (
                <input style={styles.input} placeholder="Précisez l'objectif..."
                  value={form.objectifAutre} onChange={e => setForm(f => ({ ...f, objectifAutre: e.target.value }))} autoFocus />
              )}
            </div>
          )}

          {/* ÉTAPE 3 : RÉSULTAT */}
          {etape === 3 && (
            <div style={styles.etapeWrap}>
              <div style={styles.etapeTitle}>📊 Résultat de la visite</div>
              {RESULTATS_VISITE.map(r => (
                <button key={r} style={{ ...styles.choiceBtn, ...(form.resultat === r ? styles.choiceBtnActive : {}) }}
                  onClick={() => setForm(f => ({ ...f, resultat: r }))}>{r}</button>
              ))}
              {form.resultat === 'Autre' && (
                <input style={styles.input} placeholder="Précisez le résultat..."
                  value={form.resultatAutre} onChange={e => setForm(f => ({ ...f, resultatAutre: e.target.value }))} />
              )}

              {/* SECTION COMMANDE */}
              {form.resultat === 'Commande passée' && (
                <div style={styles.commandeSection}>
                  <div style={styles.commandeTitre}>
                    🛒 Détail de la commande
                    {nbProduitsCommande > 0 && <span style={styles.commandeBadge}>{nbProduitsCommande} article(s)</span>}
                  </div>
                  {produits.length === 0 && (
                    <div style={{ fontSize: 12, color: '#888', textAlign: 'center', padding: 12 }}>
                      Aucun produit disponible — le manager doit en ajouter
                    </div>
                  )}
                  {produits.map(p => (
                    <div key={p.id} style={styles.produitRow}>
                      {p.photoUrl && (
                        <img src={p.photoUrl} alt={p.nom} style={styles.produitMiniPhoto}
                          onError={e => e.target.style.display = 'none'} />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={styles.produitNom}>{p.nom}</div>
                        {p.description && <div style={styles.produitDesc}>{p.description}</div>}
                      </div>
                      <div style={styles.qteWrap}>
                        <button style={styles.qteBtn}
                          onClick={() => updateCommande(p.id, String(Math.max(0, parseInt(commande[p.id] || 0) - 1)))}>−</button>
                        <input style={styles.qteInput}
                          value={commande[p.id] || ''}
                          onChange={e => updateCommande(p.id, e.target.value)}
                          placeholder="0" type="number" min="0" />
                        <span style={styles.qteUnite}>{p.unite || 'Sac'}</span>
                        <button style={styles.qteBtn}
                          onClick={() => updateCommande(p.id, String(parseInt(commande[p.id] || 0) + 1))}>+</button>
                      </div>
                    </div>
                  ))}

                  {/* CASE AUTRE */}
                  <div style={styles.autreRow}>
                    <div style={styles.produitNom}>✏️ Autre (commande spéciale)</div>
                    <textarea style={styles.autreInput}
                      placeholder="Décrivez la commande spéciale..."
                      value={commandeAutre}
                      onChange={e => setCommandeAutre(e.target.value)}
                      rows={2} />
                  </div>

                </div>
              )}

              {form.resultat && (
                <>
                  <div style={styles.labelSec}>Commentaire (optionnel)</div>
                  <textarea style={styles.textarea} placeholder="Ajouter un commentaire..."
                    value={form.commentaire} onChange={e => setForm(f => ({ ...f, commentaire: e.target.value }))} rows={3} />
                  <div style={styles.vocalRow}>
                    {!enregistrement && !audioUrl && (
                      <button style={styles.vocalBtn} onClick={demarrerVocal}>🎙️ Enregistrer un vocal (max 60 s)</button>
                    )}
                    {enregistrement && (
                      <button style={{ ...styles.vocalBtn, background: '#FCEBEB', color: COULEUR }} onClick={arreterVocal}>
                        ⏹ Arrêter ({dureeVocal}s / 60s)
                      </button>
                    )}
                    {audioUrl && (
                      <div style={styles.audioWrap}>
                        <audio src={audioUrl} controls style={{ width: '100%', height: 36 }} />
                        <button style={styles.deleteAudio} onClick={() => { setAudioUrl(null); setAudioBlob(null); }}>✕</button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ÉTAPE 4 : PROCHAINE ACTION */}
          {etape === 4 && (
            <div style={styles.etapeWrap}>
              <div style={styles.etapeTitle}>➡️ {t(lang, 'prochaine_action')}</div>
              {PROCHAINES_ACTIONS.map(a => (
                <button key={a} style={{ ...styles.choiceBtn, ...(form.prochaineAction === a ? styles.choiceBtnActive : {}) }}
                  onClick={() => setForm(f => ({ ...f, prochaineAction: a }))}>{a}</button>
              ))}
              {form.prochaineAction === 'Autre' && (
                <input style={styles.input} placeholder="Précisez l'action..."
                  value={form.prochaineActionAutre} onChange={e => setForm(f => ({ ...f, prochaineActionAutre: e.target.value }))} />
              )}
            </div>
          )}

          {/* ÉTAPE 5 : DATE + RESPONSABLE */}
          {etape === 5 && (
            <div style={styles.etapeWrap}>
              {form.prochaineAction === 'Aucune action' ? (
                <div style={styles.noAction}>
                  <div style={{ fontSize: 40 }}>✅</div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginTop: 8 }}>Aucune action planifiée</div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Cliquez sur Suivant pour valider</div>
                </div>
              ) : (
                <>
                  <div style={styles.etapeTitle}>📅 Date & Responsable</div>
                  <div style={styles.labelSec}>Date de l'action {form.resultat === 'Commande passée' ? '(= date de livraison)' : ''}</div>
                  <input type="date" style={styles.input} value={form.dateAction}
                    min={new Date().toISOString().split('T')[0]}
                    onChange={e => setForm(f => ({ ...f, dateAction: e.target.value }))} />
                  <div style={styles.labelSec}>Responsable</div>
                  {RESPONSABLES.map(r => (
                    <button key={r} style={{ ...styles.choiceBtn, ...(form.responsable === r ? styles.choiceBtnActive : {}) }}
                      onClick={() => setForm(f => ({ ...f, responsable: r }))}>{r}</button>
                  ))}
                  <div style={styles.labelSec}>Rappels</div>
                  {['La veille', '2 jours avant', '1 semaine avant'].map(r => (
                    <button key={r} style={{ ...styles.rappelBtn, ...(form.rappels.includes(r) ? styles.rappelBtnActive : {}) }}
                      onClick={() => toggleRappel(r)}>
                      {form.rappels.includes(r) ? '✓ ' : ''}{r}
                    </button>
                  ))}
                </>
              )}
            </div>
          )}

          {/* ÉTAPE 6 : RÉCAPITULATIF */}
          {etape === 6 && (
            <div style={styles.etapeWrap}>
              <div style={styles.motiv}>{motiv[Math.floor(Math.random() * motiv.length)]}</div>
              <div style={styles.etapeTitle}>Récapitulatif</div>
              {[
                { label: 'Client', val: client?.nom },
                { label: 'Statut', val: form.statutClient },
                { label: 'Objectif', val: form.objectifVisite === 'Autre' ? form.objectifAutre : form.objectifVisite },
                { label: 'Résultat', val: form.resultat === 'Autre' ? form.resultatAutre : form.resultat },
                { label: t(lang, 'prochaine_action'), val: form.prochaineAction === 'Autre' ? form.prochaineActionAutre : form.prochaineAction },
                { label: 'Date livraison', val: form.resultat === 'Commande passée' ? form.dateAction : null },
                { label: 'Date action', val: form.resultat !== 'Commande passée' ? form.dateAction : null },
                { label: 'Responsable', val: form.responsable || '-' },
                { label: 'Durée visite', val: `${Math.round((new Date() - heureDebut) / 60000)} min` },
              ].map((r, i) => r.val && (
                <div key={i} style={styles.recapRow}>
                  <span style={styles.recapLabel}>{r.label}</span>
                  <span style={styles.recapVal}>{r.val}</span>
                </div>
              ))}

              {/* Résumé commande */}
              {form.resultat === 'Commande passée' && nbProduitsCommande > 0 && (
                <div style={styles.recapCommande}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: VERT, marginBottom: 6 }}>🛒 Commande ({nbProduitsCommande} article(s))</div>
                  {produits.filter(p => commande[p.id]).map(p => (
                    <div key={p.id} style={{ fontSize: 12, color: '#444', marginBottom: 2 }}>
                      • {p.nom} : {commande[p.id]} {p.unite || 'Sac'}
                    </div>
                  ))}
                  {commandeAutre && <div style={{ fontSize: 12, color: '#444', marginBottom: 2 }}>• Autre : {commandeAutre}</div>}
                  <button style={{ ...styles.whatsappBtn, marginTop: 8 }} onClick={partagerWhatsApp}>
                    📱 Partager sur WhatsApp
                  </button>
                </div>
              )}

              {form.commentaire && <div style={styles.recapComment}>💬 {form.commentaire}</div>}
            </div>
          )}
        </div>

        <div style={styles.footer}>
          {etape > 1 && (
            <button style={styles.btnRetour} onClick={() => setEtape(e => e - 1)}>← Retour</button>
          )}
          {etape < 6 ? (
            <button style={{ ...styles.btnSuivant, opacity: peutAvancer() ? 1 : 0.4 }}
              onClick={() => peutAvancer() && setEtape(e => e + 1)}>
              Suivant →
            </button>
          ) : (
            <button style={styles.btnEnregistrer} onClick={enregistrerVisite} disabled={loading}>
              {loading ? 'Enregistrement...' : '✅ Enregistrer la visite'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'flex-end' },
  modal: { background: '#fff', width: '100%', maxWidth: 480, margin: '0 auto', borderRadius: '20px 20px 0 0', maxHeight: '92vh', display: 'flex', flexDirection: 'column' },
  header: { padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #f0f0f0' },
  closeBtn: { background: '#f5f5f5', border: 'none', borderRadius: '50%', width: 32, height: 32, fontSize: 14, cursor: 'pointer', flexShrink: 0 },
  headerInfo: { flex: 1 },
  headerTitle: { fontSize: 14, fontWeight: 600, color: '#1C2833' },
  headerSub: { fontSize: 12, color: '#888', marginTop: 2 },
  etapeInfo: { fontSize: 13, fontWeight: 500, color: COULEUR },
  progressBar: { display: 'flex', gap: 6, padding: '10px 16px', alignItems: 'center' },
  progressDot: { flex: 1, height: 4, borderRadius: 99, transition: 'background 0.3s' },
  content: { flex: 1, overflowY: 'auto', padding: '12px 16px' },
  etapeWrap: { display: 'flex', flexDirection: 'column', gap: 8 },
  etapeTitle: { fontSize: 16, fontWeight: 600, color: '#1C2833', marginBottom: 4 },
  etapeSub: { fontSize: 12, color: '#888', marginBottom: 8 },
  choiceBtn: { padding: '12px 14px', border: '1.5px solid #e0e0e0', borderRadius: 10, background: '#fff', color: '#444', fontSize: 14, cursor: 'pointer', textAlign: 'left' },
  choiceBtnActive: { border: `1.5px solid ${COULEUR}`, background: '#FCEBEB', color: COULEUR, fontWeight: 600 },
  input: { padding: '11px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, fontFamily: 'system-ui', width: '100%', boxSizing: 'border-box' },
  textarea: { padding: '11px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, fontFamily: 'system-ui', width: '100%', resize: 'none', boxSizing: 'border-box' },
  labelSec: { fontSize: 12, color: '#888', fontWeight: 500, marginTop: 8 },
  vocalRow: { marginTop: 4 },
  vocalBtn: { width: '100%', padding: '10px', border: '1px dashed #ccc', borderRadius: 8, background: '#f9f9f9', color: '#666', fontSize: 13, cursor: 'pointer' },
  audioWrap: { display: 'flex', alignItems: 'center', gap: 6 },
  deleteAudio: { background: '#FCEBEB', border: 'none', borderRadius: '50%', width: 28, height: 28, color: COULEUR, cursor: 'pointer', flexShrink: 0 },
  rappelBtn: { padding: '9px 14px', border: '1px solid #e0e0e0', borderRadius: 8, background: '#fff', color: '#666', fontSize: 13, cursor: 'pointer', textAlign: 'left' },
  rappelBtnActive: { border: `1px solid ${VERT}`, background: '#EAF3DE', color: VERT, fontWeight: 500 },
  noAction: { textAlign: 'center', padding: '2rem 1rem' },
  motiv: { textAlign: 'center', fontSize: 24, padding: '1rem', background: '#EAF3DE', borderRadius: 12, marginBottom: 8 },
  recapRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f5f5f5' },
  recapLabel: { fontSize: 12, color: '#888' },
  recapVal: { fontSize: 13, fontWeight: 500, color: '#1C2833', textAlign: 'right', maxWidth: '60%' },
  recapComment: { fontSize: 12, color: '#666', background: '#f9f9f9', borderRadius: 8, padding: '8px 12px', marginTop: 8 },
  recapCommande: { background: '#EAF3DE', borderRadius: 10, padding: '12px', marginTop: 8 },
  footer: { padding: '12px 16px', borderTop: '1px solid #f0f0f0', display: 'flex', gap: 8 },
  btnRetour: { padding: '12px 16px', border: '1px solid #ddd', borderRadius: 10, background: '#fff', color: '#666', fontSize: 14, cursor: 'pointer' },
  btnSuivant: { flex: 1, padding: 12, background: COULEUR, border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  btnEnregistrer: { flex: 1, padding: 12, background: VERT, border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  // Commande styles
  commandeSection: { background: '#f9f9f9', borderRadius: 10, padding: '12px', border: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', gap: 8 },
  commandeTitre: { fontSize: 14, fontWeight: 600, color: '#1C2833', display: 'flex', alignItems: 'center', gap: 8 },
  commandeBadge: { fontSize: 11, background: VERT, color: '#fff', borderRadius: 99, padding: '2px 8px', fontWeight: 600 },
  produitRow: { background: '#fff', borderRadius: 8, padding: '10px', display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #eee' },
  produitMiniPhoto: { width: 40, height: 40, borderRadius: 6, objectFit: 'cover', flexShrink: 0 },
  produitNom: { fontSize: 13, fontWeight: 600, color: '#1C2833' },
  produitDesc: { fontSize: 11, color: '#888', marginTop: 2 },
  qteWrap: { display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 },
  qteBtn: { width: 28, height: 28, border: '1px solid #ddd', borderRadius: 6, background: '#f5f5f5', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  qteInput: { width: 40, height: 28, border: '1px solid #ddd', borderRadius: 6, textAlign: 'center', fontSize: 13, fontFamily: 'system-ui' },
  qteUnite: { fontSize: 10, color: '#888', minWidth: 24 },
  autreRow: { background: '#fff', borderRadius: 8, padding: '10px', border: '1px dashed #ccc', display: 'flex', flexDirection: 'column', gap: 6 },
  autreInput: { padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, fontFamily: 'system-ui', resize: 'none', width: '100%', boxSizing: 'border-box' },
  whatsappBtn: { width: '100%', padding: '11px', background: '#25D366', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};
